import React, { useState, useEffect } from 'react';

import Grid from '@mui/material/Grid2';

import { 
  Spell, 
  BonusSpells
} from "@dndModels";
import { 
  CHARACTER_LEVELS, 
} from "@dndModels/Constants";

import API from "@utilities/smart_api";
import CommonUtilities from "@utilities/common_utilities";

import SelectStringBox from "@components/input/selects/SelectStringBox";
import CheckBox from "@components/input/CheckBox";
import ToggleButtonBox from "@components/input/buttons/ToggleButtonBox";
import LoadingBox from "@components/input/LoadingBox";

import DisplayObjects from "@dndComponents/display/DisplayObjects";
import FilterBox from "@dndComponents/filtering/FilterBox";
import ViewSpell from "@dndComponents/display/ViewSpell";
import SelectObjectBox from "@components/input/selects/SelectObjectBoxSimple";
import LetterLinks from "@components/Navigation/LetterLinks";
import PageLinks from "@components/Navigation/PageLinks";
import ButtonBox from "@components/input/buttons/ButtonBox";
import StringBox from "@components/input/strings/StringBox";
import FlexBox from '@components/layout/FlexBox';
import DisplayStringWithReplacements from '@dndComponents/display/DisplayStringWithReplacements';
import DandDButtonBox from '@dndComponents/DandDButtonBoxSimple';

interface Props {
  obj: BonusSpells;
  onChange: (changed: BonusSpells) => void; 
  disabled: boolean;
}

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();

export default function BonusSpellsFeatureInput({ 
  obj,
  onChange,
  disabled
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [spells, setSpells] = useState<Spell[]>([]);
  const [page_num, setPageNum] = useState(0);
  // const [level, setLevel] = useState("ALL");
  // const [school, setSchool] = useState("ALL");
  const [startLetter, setStartLetter] = useState("");
  // const [search_string, setSearchString] = useState("");
  const [on_list, setOnList] = useState(false);
  const [filter, setFilter] = useState<any>({});
  const [expanded, setExpanded] = useState("");

  useEffect(() => {
    if (!fetched) {
      load()
    }
  });

  const load = () => {
    setLoading(true);
    api.getObjects({ data_type: "spell" }).then(res => {
      setSpells(res as Spell[]);
      setLoading(false);
    });
    setFetched(true);
  }
  const updateFilter = (the_filter: any) => {
    setFetched(true);
    const new_filter = {...the_filter};
    
    if (startLetter !== "") {
      new_filter["name*startsWith"] = startLetter;
    } else {
      delete new_filter["name*startsWith"];
    }
    const source_book = new_filter.source_book;
    if (source_book && Object.keys(source_book).length > 0) {
      new_filter.source_book_ids = source_book;
    }
    delete new_filter.source_book;
    const spell_level = new_filter.spell_level;
    if (spell_level && Object.keys(spell_level).length > 0) {
      if (spell_level.in) {
        const arr = spell_level.in;
        new_filter.level = {
          in: arr.map((o: any) => {
            return o === "Cantrip" ? 0 : +o;
          })
        }
      } else if (spell_level.nin) {
        const arr = spell_level.nin;
        new_filter.level = {
          nin: arr.map((o: any) => {
            return o === "Cantrip" ? 0 : +o;
          })
        }
      }
    }
    delete new_filter.spell_level;
    const effect_type = new_filter.effect_type;
    if (effect_type && Object.keys(effect_type).length > 0) {
      new_filter.effect_types = effect_type;
    }
    delete new_filter.effect_type;
    const attack_type = new_filter.attack_type;
    if (attack_type && Object.keys(attack_type).length > 0) {
      new_filter.attack_types = attack_type;
    }
    delete new_filter.attack_type;
    const target_type = new_filter.target_type;
    if (target_type && Object.keys(target_type).length > 0) {
      new_filter.target_types = target_type;
    }
    delete new_filter.target_type;
    const spell_list = new_filter.spell_list;
    if (spell_list && Object.keys(spell_list).length > 0) {
      new_filter.spell_list_ids = spell_list;
    }
    delete new_filter.spell_list;
    // const magic_element = new_filter.magic_element;
    // if (magic_element && Object.keys(magic_element).length > 0) {
    //   new_filter.element_ids = magic_element;
    // }
    // delete new_filter.magic_element;

    setFilter(new_filter);
    setPageNum(0);
    setStartLetter("");
  }

  const sort = (obj1: Spell, obj2: Spell) => {
    if (obj1 instanceof Spell && obj2 instanceof Spell) {
      return obj1.level > obj2.level ? 1 : (obj1.level === obj2.level ? obj1.use_name.trim().localeCompare(obj2.use_name.trim()) : -1);
    }
    return 0;
  }

  const renderSpells = () => {
    const page_size = 7;
    let filtered = spells ? (api.filter("spell", spells, filter) as Spell[]).sort(sort) : [];
    if (on_list) {
      const spell_ids = obj.spell_ids;
      filtered = filtered.filter(o => spell_ids.includes(o._id) || common_util.anyOverlaps(o.variant_ids, spell_ids));
    } 
    if (startLetter !== "") {
      filtered = filtered.filter(o => o.name.startsWith(startLetter));
    }
    const page_count = Math.ceil(filtered.length / page_size);
    const filtered_and_paged = filtered.slice(page_size * page_num, page_size * (page_num + 1));
    return (
      <Grid container spacing={0} direction="row">
        <Grid size={12} className="header">
          Spells
        </Grid>
        <Grid size={12}>
          <FilterBox
            data_type="spell"
            all_objects={spells}
            quick_fields={["school","spell_level","spell_list"]}
            onChange={updateFilter}
            onLoad={updateFilter}
          />
        </Grid>
        <Grid size={6}>
          <ToggleButtonBox
            name="On List"
            value={on_list}
            onToggle={() => { setOnList(!on_list); }}
          />
        </Grid>
        <Grid size={6}>
          <ButtonBox
            name="Remove Orphaned"
            onClick={() => { 
              const new_obj = obj;
              const all_spell_ids = spells.map(o => o._id);
              new_obj.spells = new_obj.spells.filter(o => all_spell_ids.includes(o.spell_id));
              onChange(new_obj);
            }}
          />
        </Grid>
        <Grid size={12}>
          <PageLinks selected={page_num} page_count={page_count} onChange={setPageNum} />
        </Grid>
        <Grid size={12} container spacing={0} direction="row" className="header">
          <Grid size={6}>
            Spell
          </Grid>
          <Grid size={4}>
            Source Books
          </Grid>
          <Grid size={2}>
            For Level
          </Grid>
        </Grid>
        { filtered_and_paged.map((spell, key) => {
          const alternate = key % 2 === 1;
          const className = `object_index_item ${disabled ? "disabled" : ""} ${(alternate ? "alternate" : "")}`;
          // const labelClassName = `labs_label ${disabled ? "disabled" : "clickable"} ${(alternate ? "alternate" : "")}`;
          // if (spell.name.includes("Darkness") && obj.spell_ids.includes(spell._id)) {
          //   console.log(obj.spell_level(spell._id));
          // }
          return (
            <Grid
              key={spell._id}
              className={className}
              size={{
                xs: 12
              }}>
              <FlexBox direction="column">
                <FlexBox direction="row">
                  <FlexBox xs_expand={0}>
                    <CheckBox 
                      disabled={disabled}
                      value={ obj.spell_ids.includes(spell._id) }
                      onChange={(changed: boolean) => {
                        const new_obj = obj;
                        if (changed) {
                          const entry = new_obj.spells.find(o => o.spell_id === spell._id);
                          if (!entry) {
                            new_obj.spells.push({ spell_id: spell._id, level: 1 });
                            onChange(new_obj);
                          }
                        } else {
                          new_obj.spells = new_obj.spells.filter(o => o.spell_id !== spell._id);
                          onChange(new_obj);
                        }
                      }}
                    />
                  </FlexBox>
                  <FlexBox xs_expand={0} className="clickable" style={{ width: "50%" }} onClick={() => setExpanded(expanded === spell._id ? "" : spell._id)}>
                    <ViewSpell
                      spell={spell}
                    />
                  </FlexBox>
                  <FlexBox>
                    <DisplayObjects type="source_book" display_field="abbreviation" ids={spell.source_book_ids} />
                  </FlexBox>
                  <FlexBox xs_expand={0} className="float-right">
                    { obj.spell_ids.includes(spell._id) &&
                      <SelectStringBox
                        name="Level"
                        disabled={disabled}
                        value={`${obj.spell_level(spell._id)}`}
                        options={CHARACTER_LEVELS}
                        onChange={(changed: string) => {
                          const new_obj = obj;
                          const entry = new_obj.spells.find(o => o.spell_id === spell._id);
                          if (entry) {
                            entry.level = +changed;
                            onChange(new_obj);
                          }
                        }}
                      />
                    }
                  </FlexBox>
                </FlexBox>
                { expanded === spell._id && 
                  <FlexBox className="bordered blackBackground">
                    <DisplayStringWithReplacements display_string={spell.description} />
                  </FlexBox>
                } 
              </FlexBox>
            </Grid>
          );
        }) }
        <Grid
          size={{
            xs: 12
          }}>
          <PageLinks selected={page_num} page_count={page_count} onChange={setPageNum} />
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <LetterLinks onChange={setStartLetter} />
        </Grid>
      </Grid>
    ); 
  }

  const addTableToDescription = () => {
    let table_string = "\n\n";
    table_string += `<b>${obj.name}</b>\n`;
    table_string += `<table>\n`;
    table_string += `<thead>\n`;
    table_string += `<tr><th>Level</th><th>Prepared Spells</th></tr>\n`;
    table_string += `</thead>\n`;
    table_string += `<tbody>\n`;
    const levels = common_util.unique_array(obj.spells.map(o => o.level));
    for (const level of levels) {
      const spells = obj.spells.filter(o => o.level === level);
      const spell_ids = spells.map(o => o.spell_id);
      const the_spells = api.smart_hash.spell.filter(o => spell_ids.includes(o._id));
      table_string += `<tr><td>${level}</td><td>`;
      table_string += the_spells.map(o => o.name).join(", ");
      table_string += `</td></tr>\n`;
    }
    table_string += `</tbody>\n`;
    table_string += `</table>\n`;
    obj.description += table_string;
    onChange(obj);
  }

  if (!fetched || loading) {
    return (<LoadingBox />);
  } else {
    return (
      <Grid container spacing={0} direction="row">
        <Grid size={4}>
          <CheckBox 
            name="Always Known/Prepared"
            value={obj.always_known}
            disabled={disabled}
            onChange={(changed: boolean) => {
              const new_obj = obj;
              new_obj.always_known = changed;
              onChange(new_obj);
            }}
          />
        </Grid>
        <Grid size={4}>
          <StringBox 
            name="Upgrade Key"
            value={obj.upgrade_key}
            disabled={disabled}
            onBlur={(changed: string) => {
              const new_obj = obj;
              new_obj.upgrade_key = changed;
              onChange(new_obj);
            }}
          />
        </Grid>
        <Grid size={4}>
          <DandDButtonBox 
            name="Add Table to Description"
            disabled={disabled}
            onClick={addTableToDescription}
          />
        </Grid>
        <Grid size={12}>
          <SelectObjectBox
            name="Count as Being From Class" 
            data_type="game_class"
            value={ obj.count_as_class_id } 
            disabled={disabled}
            auto_sort
            dominant_only
            onChange={(id: string) => {
              const new_obj = obj;
              new_obj.count_as_class_id = id;
              onChange(new_obj);
            }} 
          />
        </Grid>
        <Grid size={12}>
          { renderSpells() }
        </Grid>
      </Grid>
    );
  }
}
